import { firstValueFrom, Observable, of } from 'rxjs';
import { LongOpToolBarViewModel } from './long-op-tool-bar-view-model';
import { LogService } from '@nts/std/utility';
import { LongOpApiClient } from '../../api-clients/long-op/long-op-api-client';
import { BaseLongOpModel } from '../../domain-models/base-long-op-model';
import { CoreOrchestratorViewModel } from '../core-orchestrator-view-model';
import { ToolBarViewModelInterface } from '../tool-bar-view-model.interface';
import { CommandTypes } from '../commands/command-types';
import { RoutingService } from '../../routing/routing.service';
import { MessageResourceManager } from '../../resources/message-resource-manager';
import { MessageCodes } from '../../resources/message-codes';
import { plainToClass } from '@nts/std/serialization';
import { GenericServiceRequest } from '../../requests/generic-service-request';
import { LongOpRootViewModel } from './long-op-root-view-model';
import { ClassAdditionalInfo, MsgClearMode, ViewModelStates } from '@nts/std/types';
import type { BaseErrorInterface, CreateResponseInterface, GenericServiceResponseInterface, IdentityInterface, LongOpOrchestratorViewModelInterface, MessageContainerInterface, ModelInterface, ServiceRequestInterface, ServiceResponseInterface, StatusMessageInterface } from '@nts/std/interfaces';
import { InternalViewModel } from '../aggregate-element-view-model';
import { ServiceResponse } from '../../responses/service-response';
import { GenericServiceResponse } from '../../responses/generic-service-response';
import { CreateResponse } from '../../responses/create-response';
import { BaseError } from '@nts/std/messages';

export class LongOpOrchestratorViewModel<
  TRootViewModel extends LongOpRootViewModel<TModel, TIdentity, TParams, TResult, TParamsModel, TResultModel>,
  TApiClient extends LongOpApiClient<TModel, TIdentity, TParamsModel, TResultModel>,
  TModel extends BaseLongOpModel<TIdentity, TParamsModel, TResultModel>,
  TIdentity extends IdentityInterface,
  TParams extends InternalViewModel<TParamsModel, TIdentity>,
  TResult extends InternalViewModel<TResultModel, TIdentity>,
  TParamsModel extends ModelInterface<TIdentity>,
  TResultModel extends ModelInterface<TIdentity>>
  extends CoreOrchestratorViewModel<TRootViewModel, TApiClient, TModel, TIdentity>
  implements LongOpOrchestratorViewModelInterface<TRootViewModel> {

  classAdditionalInfo?: ClassAdditionalInfo.LongOp;

  override showCurrentState = false;

  getToolBarMenu(): ToolBarViewModelInterface {
    return new LongOpToolBarViewModel(this);
  }

  override removeError(item: MessageContainerInterface) {

  }

  override async checkIfPendingChanges(): Promise<boolean> {
    // #3929 disabilito il controllo dello stato del documento quando è una longop
    return Promise.resolve(false);
  }

  override async checkStatus(): Promise<StatusMessageInterface> {
    const status = await super.checkStatus();
    status.pendingChanges = await this.checkIfPendingChanges();
    return status;
  }

  override checkIfCanUnload(): boolean {
    // #3929 disabilito il controllo dello stato del documento quando è una longop
    return true;
  }

  async create(
    force?: boolean,
    additionalErrors: BaseErrorInterface[] = []
  ): Promise<ServiceResponseInterface> {
    let response: ServiceResponseInterface = new ServiceResponse();
    response.operationSuccedeed = false;
    const result = force || await this.confirmLosingUnsavedDataAsync();
    if (result) {
      try {
        if (this.actionInProgress == false) {
          this.eventDispatcher.onActionInProgress.next(true);
        }
        response = await this.createWithClearAsync(true);
        LogService.debug(MessageResourceManager.Current.getMessage('CreateLongOpCompleted'));
      } catch (error) {
        const baseError = new BaseError();
        baseError.description = error?.message;
        baseError.stackTrace = error?.stack;
        response.errors.push(baseError);

        LogService.warn(MessageResourceManager.Current.getMessage('CreateLongOpFailed'), error);
      } finally {
        this.eventDispatcher.onActionInProgress.next(false);
      }
    }
    return response;
  }

  async createRootEntityAsync(): Promise<CreateResponseInterface<TModel, TIdentity>> {
    return await firstValueFrom(this.apiClient.create());
  }

  async executeLongOp(): Promise<GenericServiceResponseInterface<TResultModel>> {
    let response: GenericServiceResponseInterface<TResultModel> = null;
    try {
      if (this.actionInProgress == false) {
        this.eventDispatcher.onActionInProgress.next(true);
      }
      response = await this.executeLongOpImplementationAsync(true);
      LogService.debug(MessageResourceManager.Current.getMessage('ExecuteLongOpCompleted'));
    } catch (error) {
      LogService.error(MessageResourceManager.Current.getMessage('ExecuteLongOpFailed'), error);
    } finally {
      this.eventDispatcher.onActionInProgress.next(false);
    }
    return response;
  }

  async getByJsonObject(jsonObject: string, additionalErrors: BaseErrorInterface[] = []): Promise<ServiceResponseInterface> {
    // di default considera il JsonObject una identity
    const object = plainToClass<TIdentity, TIdentity>(
      this.identityType,
      JSON.parse(jsonObject) as TIdentity
    );
    return await this.getByObject<TIdentity>(object, additionalErrors);
  }

  async getByObject<TObject>(object: TObject, additionalErrors: BaseErrorInterface[] = []): Promise<ServiceResponseInterface> {
    let response: ServiceResponseInterface = new ServiceResponse();
    response.operationSuccedeed = false;
    if (await firstValueFrom(this.currentState.canGetByObject())) {
      try {
        if (this.actionInProgress == false) {
          this.eventDispatcher.onActionInProgress.next(true);
        }
        response = await this.getByObjectImplementationAsync<TObject>(object, additionalErrors);
      } catch (e) {
        LogService.warn('getByObject failed', e);
      } finally {
        this.eventDispatcher.onActionInProgress.next(false);
      }
      return response;
    } else {
      const error = new BaseError();
      error.code = 'NotAllowed';
      error.description = MessageResourceManager.Current.getMessageWithStrings(MessageCodes.CommandNotAllowed,
        MessageResourceManager.Current.getMessage('std_CMD_' + CommandTypes[CommandTypes.GetByObject]));
      response.errors = [error];
      this.notAllowedAction(CommandTypes.GetByObject);
      return response;
    }
  }

  async loadByObjectImplementationAsync<TObject>(object: TObject, additionalErrors: BaseErrorInterface[] = []): Promise<GenericServiceResponseInterface<TModel>> {

    // TODO Tommy gestione rootViewModel virtuale
    // Crea un rootViewModel vuoto così da renderizzare l'interfaccia mentre viene fatta la chiamata
    // const entityClass = NTSReflection.getClassMetadata('responseDomainModelType', this.apiClient);
    // const newEntity = this.createMockEntity<TEntity>(entityClass, this.metadata);
    // await this.tryRebuildViewModelAsyncWithoutResponse(newEntity, false, true);

    return firstValueFrom(this.getByObjectFromApiClient(object))
      .then(async (response) => {
        this.handleAdditionalErrors(response, additionalErrors);
        if (response.operationSuccedeed && response.result != null) {
          await this.tryRebuildViewModelAsync(response, response.result, false, true);
          // TODO vedere se serve
          // await this.setCurrentStateFromGetByIdentityResponse(response);

          this.updateParams(object);
          const valid = this.viewModelValidate(false);
          if (!valid) {
            this.eventDispatcher.onValidationBarCollapsed.next(false);
          }

        } else {
          this.refreshMessagesFromResponse(response, MsgClearMode.ClearOnlyTemporaryMessage);
        }
        return response;
      }).catch(err => {
        LogService.debug('loadByIdentityImplementationAsync long op failed', err);
        const response = new GenericServiceResponse<TModel>(this.apiClient.rootModelType);
        response.operationSuccedeed = false;
        return response;
      });
  }

  // Can Actions
  override canGenerateReport(): Observable<boolean> {
    return of(true);
  }

  canCreate(x: any): Observable<boolean> {
    return this.currentState.canCreate();
  }

  canExecuteLongOp(): Observable<boolean> {
    return of(true);
  }

  // isVisible

  isVisibleExecuteLongOp(): Observable<boolean> {
    return of(true);
  }

  // fare override del metodo per modificare il comportamento della maschera
  // e aprire in modalità lettura passando un oggetto generico
  getJsonObjectFromRouteParam(routeParam: string): string | null {
    return routeParam ? RoutingService.decodeUrl(routeParam) : null;
  }

  protected async createWithClearAsync(clearPreviousMessages: boolean, additionalErrors: BaseErrorInterface[] = []): Promise<CreateResponseInterface<TModel, TIdentity>> {
    const createResponse = await this.createImplementationAsync(clearPreviousMessages, additionalErrors);
    // aggiorno la rotta corrente rimuovento l'eventuale identity
    this.updateParams();
    // Disabilito la validazione in fase di creazione
    // this.viewModelValidate(false);
    return createResponse;
  }

  protected async preCreate(): Promise<void> {

  }

  protected async postCreate(): Promise<void> {

  }

  protected async createImplementationAsync(clearPreviousMessages: boolean, additionalErrors: BaseErrorInterface[] = []): Promise<CreateResponseInterface<TModel, TIdentity>> {

    let createResponse: CreateResponseInterface<TModel, TIdentity> = new CreateResponse<TModel, TIdentity>(this.apiClient.rootModelType);
    createResponse.operationSuccedeed = false;
    if (await firstValueFrom(this.canCreate(null))) {

      // TODO Tommy: crea rootViewModel virtual
      // Crea un rootViewModel vuoto così da renderizzare l'interfaccia mentre viene fatta la chiamata
      // const entityClass = NTSReflection.getClassMetadata('domainModelEntityType', this.apiClient);
      // const newEntity = this.createMockEntity<TEntity>(entityClass, this.metadata);
      // await this.tryRebuildViewModelAsyncWithoutResponse(newEntity, true, true);
      await this.preCreate();

      try {
        createResponse = await this.createRootEntityAsync();
      } catch (catchedError) {
        const errFromCreateRootEntityAsync: BaseErrorInterface = new BaseError();
        const message = catchedError.message ? catchedError.message : catchedError.toString();
        errFromCreateRootEntityAsync.code = 'GenericError';
        errFromCreateRootEntityAsync.description = message;
        errFromCreateRootEntityAsync.stackTrace = catchedError.stack;
        LogService.log('Errore durante la createRootEntityAsync', catchedError);
        createResponse.errors.push(errFromCreateRootEntityAsync);
      }

      this.handleAdditionalErrors(createResponse, additionalErrors);

      const hasErrors = createResponse.errors.length > 0;
      // Se non ci sono errori con status code differenti da 200
      const hasNonBlockingErrors = createResponse.errors.every((e) =>
        e.responseInfo?.status == null || e.responseInfo?.status / 100 == 2
      );

      if (
        !hasErrors
        || (
          hasErrors
          && hasNonBlockingErrors
          && !this.forceStopRenderPageAfterError
        )
      ) {
        // Se operationSuccedeed è false e il result non è stato popolato, lo popolo con una versione mocckata in modo da poter visualizzare correttamente la maschera
        if (createResponse.operationSuccedeed === false && createResponse.result == null) {
          createResponse.result = this.createMockDomainModel(this.apiClient.rootModelType, this.metadata);
        }
        await this.tryRebuildViewModelAsync(createResponse, createResponse.result, true, clearPreviousMessages);
        this.currentState.create();
        await this.postCreate();
      } else {
        LogService.debug('createImplementationAsync failed', createResponse);
        // this.toastMessageService.showToastsFromResponse(response);
      }
    } else {
      const error = new BaseError();
      error.code = 'NotAllowed';
      error.description = MessageResourceManager.Current.getMessageWithStrings(MessageCodes.CommandNotAllowed,
        MessageResourceManager.Current.getMessage('std_CMD_' + CommandTypes[CommandTypes.Create]));
      createResponse.errors = [error];
      this.notAllowedAction(CommandTypes.Create);
    }
    return createResponse;
  }

  protected async getByObjectImplementationAsync<TObject>(object: TObject, additionalErrors: BaseErrorInterface[] = []): Promise<ServiceResponseInterface> {
    const response = await this.loadByObjectImplementationAsync<TObject>(object, additionalErrors);
    if (!this.getByObjectIsSuccessfully(response)) {
      // se non è stato trovato nulla allora passo allo stato di New. Se però sono
      // già nello stato di new allora non faccio nulla
      if (this.currentState.value !== ViewModelStates.New) {
        return await this.createWithClearAsync(false, additionalErrors);
      }
    }
    return response;
  }

  protected getByObjectIsSuccessfully(response: GenericServiceResponseInterface<TModel>) {
    return response.operationSuccedeed && response.result != null;
  }

  protected getByObjectRequest<TObject>(object: TObject): ServiceRequestInterface {
    const request: GenericServiceRequest<TObject> = new GenericServiceRequest<TObject>();
    request.requestData = object;
    return request;
  }

  protected getByObjectResponse(): GenericServiceResponseInterface<TModel> {
    return new GenericServiceResponse<TModel>(this.apiClient.rootModelType);
  }

  protected getByObjectUri(): string {
    return 'GetByObject';
  }

  protected getByObjectFromApiClient<TObject>(object: TObject): Observable<GenericServiceResponseInterface<TModel>> {
    return this.apiClient.getByObject(
      this.getByObjectRequest<TObject>(object),
      this.getByObjectResponse(),
      this.getByObjectUri()
    );
  }

  protected async executeLongOpImplementationAsync(clearPreviousMessages: boolean): Promise<GenericServiceResponseInterface<TResultModel>> {
    let response: GenericServiceResponseInterface<TResultModel> = new GenericServiceResponse<TResultModel>(this.domainModel.longOpResultType);
    response.operationSuccedeed = false;

    if (this.viewModelValidate(true)) {
      response = await firstValueFrom(this.apiClient.executeLongOp(this.rootViewModel.getDomainModel()));

      this.showFromResponse(response, MsgClearMode.ClearAllMessages);

      if (response.operationSuccedeed && response.result != null) {
        const dm = this.rootViewModel.getDomainModel();
        dm.result = response.result;
        await this.tryRebuildViewModelAsync(response, dm, true, clearPreviousMessages);

        this.currentState.modify();
      } else {
        LogService.debug('executeLongOpImplementationAsync failed', response);
      }
    } else {
      this.eventDispatcher.onValidationBarCollapsed.next(false);
    }

    return response;
  }
}
